import { useQuery } from "@tanstack/react-query";
import { useAuthenticatedFetch } from "@/hooks/use-authenticated-fetch";
import { OFFLINE_DURABLE_GC_TIME, creatorKeys } from "@/lib/query-keys";
import { isPinnedSync } from "@/offline/pins";
import type { RecordCreator } from "@/types/api";

/**
 * Fetches the distinct users who have created at least one record in the
 * project — the people selectable in the "Created by" filter. The endpoint is
 * not paginated; it returns the full list in a single response.
 */
export function useRecordCreators(projectId: string) {
  const fetchWithAuth = useAuthenticatedFetch();

  return useQuery({
    queryKey: creatorKeys.list(projectId),
    queryFn: () =>
      fetchWithAuth<RecordCreator[]>(
        `/projects/1.0/projects/${projectId}/record-creators/`,
      ),
    staleTime: 30_000,
    // GC-immune only while pinned — kept in lockstep with the persister
    // allowlist (`shouldDehydrateQuery`). Non-pinned projects use the default.
    gcTime: isPinnedSync(projectId) ? OFFLINE_DURABLE_GC_TIME : undefined,
  });
}
